import { useEffect, useState } from 'react';
import type { ResponseAssetDetail, ResponseAssetFeature } from '@tsr/shared-types';
import { fetchResponseAsset } from './api';
import { classifyAsset } from './assetStyles';

/**
 * Side-panel detail for a response asset (parallel to CameraMedia). Renders the
 * list feature immediately, then enriches with the detail payload (metadata,
 * child assets, tracked aircraft). Illustrative assets get a prominent banner.
 *
 * `variant='embedded'` drops the outer banner + name/kind header so the panel
 * can be slotted beneath DispatchCapacityPanel (which supplies its own header)
 * without duplicating it (V.3 §6).
 */
export function AssetMedia({
  asset,
  variant = 'full',
}: {
  asset: ResponseAssetFeature;
  variant?: 'full' | 'embedded';
}): JSX.Element {
  const [detail, setDetail] = useState<ResponseAssetDetail | null>(null);
  const p = asset.properties;
  const style = classifyAsset(p);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    void fetchResponseAsset(asset.id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [asset.id]);

  const aircraft = detail?.properties.aircraft ?? [];
  const children = detail?.properties.children ?? [];
  const note =
    (detail?.properties.metadata?.illustrative_note as string | undefined) ??
    (p.illustrative ? 'Example location pending a data partnership with MEPR.' : undefined);

  const embedded = variant === 'embedded';

  return (
    <div className={embedded ? 'px-4 pb-4 space-y-4' : 'px-4 py-4 space-y-4'}>
      {!embedded && p.illustrative && (
        <div
          data-testid="illustrative-banner"
          className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
        >
          <span className="font-semibold uppercase tracking-wider">Illustrative</span> — example
          {note ? ` ${note}` : ' data pending data partnership with MEPR.'}
        </div>
      )}

      {!embedded && (
        <div>
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ backgroundColor: style.color }}
              aria-hidden
            />
            <h2 className="text-base font-semibold flex-1">{asset.properties.name}</h2>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
              {style.kind}
            </span>
            {p.operator_org && <span className="ml-2">{p.operator_org}</span>}
          </p>
          {asset.geometry && (
            <p className="text-xs text-slate-500 mt-0.5">
              {asset.geometry.coordinates[1].toFixed(4)}°N, {asset.geometry.coordinates[0].toFixed(4)}°E
            </p>
          )}
        </div>
      )}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Details</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          {p.response_role && (
            <>
              <dt className="text-slate-400">Role</dt>
              <dd className="font-mono">{p.response_role}</dd>
            </>
          )}
          {p.service_radius_km !== null && (
            <>
              <dt className="text-slate-400">Service radius</dt>
              <dd className="font-mono">{p.service_radius_km} km</dd>
            </>
          )}
          {/* Aircraft-as-asset fields (R.1b will populate aviation_aircraft rows). */}
          {p.tail_number && (
            <>
              <dt className="text-slate-400">Tail number</dt>
              <dd className="font-mono">{p.tail_number}</dd>
            </>
          )}
          {p.icao_hex && (
            <>
              <dt className="text-slate-400">ICAO hex</dt>
              <dd className="font-mono">{p.icao_hex}</dd>
            </>
          )}
          {p.aircraft_type && (
            <>
              <dt className="text-slate-400">Type</dt>
              <dd className="font-mono">{p.aircraft_type}</dd>
            </>
          )}
          {p.engine_count !== null && (
            <>
              <dt className="text-slate-400">Engines</dt>
              <dd className="font-mono">
                {p.engine_count}
                {p.engine_count >= 2 && (
                  <span className="ml-1 text-[10px] text-sky-300">over-water capable</span>
                )}
              </dd>
            </>
          )}
          {p.vessel_class && (
            <>
              <dt className="text-slate-400">Vessel class</dt>
              <dd className="font-mono">{p.vessel_class}</dd>
            </>
          )}
          {p.vessel_count !== null && (
            <>
              <dt className="text-slate-400">Vessels</dt>
              <dd className="font-mono">{p.vessel_count}</dd>
            </>
          )}
          {p.primary_waters && (
            <>
              <dt className="text-slate-400">Primary waters</dt>
              <dd className="font-mono">{p.primary_waters}</dd>
            </>
          )}
          <dt className="text-slate-400">Status</dt>
          <dd className="font-mono">{p.status}</dd>
        </dl>
      </section>

      {aircraft.length > 0 && (
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
            Aircraft ({aircraft.length})
          </h3>
          <table className="w-full text-xs" data-testid="asset-aircraft">
            <tbody>
              {aircraft.map((a) => (
                <tr key={a.id} className="border-b border-slate-800 last:border-0">
                  <td className="py-1 pr-2 font-mono text-slate-200">{a.tail_number}</td>
                  <td className="py-1 pr-2 text-slate-400">{a.aircraft_type}</td>
                  <td className="py-1 text-right font-mono text-slate-500">
                    {a.icao_hex ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {children.length > 0 && (
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
            {p.parent_org_id === null && p.asset_class === 'umbrella_org'
              ? `Partners (${children.length})`
              : `Linked (${children.length})`}
          </h3>
          <ul className="text-xs space-y-0.5" data-testid="asset-children">
            {children.slice(0, 60).map((c) => (
              <li key={c.id} className="text-slate-300 flex items-center gap-1.5">
                <span className="text-slate-600">·</span>
                <span className="truncate">{c.name}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
